// The signed-in frame, built for this app (packages/ui AppShell has no side
// navigation). A grid sized to the viewport: the document never scrolls,
// main is the scroller, so the chrome is always in reach.
//
//   below 768    pinned header (mark and product name), main, bottom bar of
//                four destinations plus Lainnya (a sheet with the rest and
//                Profil). design review ui.component_recipe md.nav_bar_more.
//   768 to 1023  an 80px rail on layer-1: icon above the short label.
//   from 1024    a navigation drawer on layer-1: icon and full label; the
//                icon button beside the product name collapses it to the
//                rail and back (remembered per browser); Profil and Keluar
//                at the foot. design review md.nav_drawer_collapsible.
//
// The sidebar is separated from the page by its tonal step alone, never a
// side line. A screen that manages its own scrolling (the Chat screen) passes
// fill, and marks its own scroller with data-ui-scroller.
//
// The brand reads "Pluto" on one line, as in the ChatShell; the full product
// name stays the home link's accessible name.
//
// The admin console is the same shell with the admin destinations
// (shell/nav.ts) under group titles (Pengguna, Pengetahuan, Layanan; shown in
// the full sidebar only), the brand links to /admin/akun,
// Profil is /admin/profil, and the phone bar holds Akun, Pengetahuan, Profil
// with no Lainnya.
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { User } from "../api";
import { useAuth } from "../auth";
import { ROLE_LABEL } from "../lib/labels";
import { Dialog, type PageLayout } from "../ui/components";
import { Glyph } from "../ui/icons";
import { useMedia } from "../chat/support";
import { ThemeSwitch, ThemeToggle } from "./ThemeToggle";
import { PRODUCT_NAME, PRODUCT_SHORT } from "../lib/product";
import { ShellContext, type ShellApi } from "./context";
import { barSplit, groupNav, homeFor, navFor, profileItemFor, type NavItem } from "./nav";

const COLLAPSE_KEY = "procureai.nav.collapsed";

function readCollapsed(): boolean {
  try {
    return typeof localStorage !== "undefined" && localStorage.getItem(COLLAPSE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeCollapsed(value: boolean): void {
  try {
    if (value) localStorage.setItem(COLLAPSE_KEY, "1");
    else localStorage.removeItem(COLLAPSE_KEY);
  } catch {
    // Private mode: the choice lasts for this page only.
  }
}

export function Mark() {
  return (
    <span className="app-mark" aria-hidden="true">
      {/* The "P" of Pluto, the same letterform as favicon.svg. */}
      <svg viewBox="17.5 12 32 40" width="16" height="20" focusable="false">
        <path
          d="M22 48V16h12.5a10.5 10.5 0 0 1 0 21H29v11z M29 23v7h5.5a3.5 3.5 0 0 0 0-7z"
          fill="currentColor"
          fillRule="evenodd"
        />
      </svg>
    </span>
  );
}

function SideItem({ item, current }: { item: NavItem; current: string }) {
  const on = item.id === current;
  return (
    <li>
      <a
        className="side-item"
        href={item.href}
        aria-current={on ? "page" : undefined}
        aria-label={item.label}
        title={item.short !== item.label ? item.label : undefined}
      >
        <span className="side-icon">
          <Glyph name={item.icon} size={24} />
        </span>
        <span className="side-label side-label-full">{item.label}</span>
        <span className="side-label side-label-short" aria-hidden="true">
          {item.short}
        </span>
      </a>
    </li>
  );
}

export interface ShellProps {
  user: User;
  /** The nav id of the current route ("" for not found). */
  current: string;
  /** The current path, so main returns to the top on every route change. */
  path: string;
  /** The screen fills main and scrolls its own regions (the Chat screen). */
  fill?: boolean;
  /** The page width type (Fase 4): "form" is 760px wide; none is a list page (1200px). */
  layout?: PageLayout;
  children: ReactNode;
}

export function Shell({ user, current, path, fill, layout, children }: ShellProps) {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [moreOpen, setMoreOpen] = useState(false);
  const tablet = useMedia("(min-width: 768px)");
  const wide = useMedia("(min-width: 1024px)");
  const [leaving, setLeaving] = useState(false);
  const [forceRail, setForceRailState] = useState(false);
  const [headerSlot, setHeaderSlot] = useState<HTMLElement | null>(null);
  const [headerTitle, setHeaderTitleState] = useState<string | null>(null);
  const [headerHeading, setHeaderHeading] = useState(false);
  const setForceRail = useCallback((on: boolean) => setForceRailState(on), []);
  const setHeaderTitle = useCallback((t: string | null, heading = false) => {
    setHeaderTitleState(t);
    setHeaderHeading(t !== null && heading);
  }, []);
  const api = useMemo<ShellApi>(() => ({ headerSlot, setHeaderTitle, setForceRail }), [headerSlot, setHeaderTitle, setForceRail]);
  const rail = collapsed || forceRail;
  const { signOut } = useAuth();
  const main = useRef<HTMLElement>(null);
  const items = navFor(user.role);
  const home = homeFor(user.role);
  const homeName = user.role === "admin" ? `${PRODUCT_NAME}, Akun` : user.role === "konsultan" ? `${PRODUCT_NAME}, Pendampingan` : `${PRODUCT_NAME}, Chat`;
  const profileItem = profileItemFor(user.role);
  const { bar, more } = barSplit(user.role);
  const moreActive = more.some((m) => m.id === current);

  useEffect(() => {
    const el = main.current;
    if (el) el.scrollTop = 0;
    setMoreOpen(false);
  }, [path]);

  const leave = async () => {
    setLeaving(true);
    try {
      await signOut();
    } finally {
      setLeaving(false);
    }
  };

  // While a screen holds the rail, the button opens the drawer for now and
  // leaves the saved choice alone.
  const toggle = () => {
    if (forceRail && !collapsed) {
      setForceRailState(false);
      return;
    }
    setCollapsed((c) => {
      writeCollapsed(!c);
      return !c;
    });
  };

  const logoutButton = (
    <button type="button" className="ui-icon-btn side-logout" aria-label="Keluar" title="Keluar" disabled={leaving} aria-busy={leaving || undefined} onClick={leave}>
      <Glyph name="logout" />
    </button>
  );

  return (
    <ShellContext.Provider value={api}>
    <div className="app" data-collapsed={rail ? "" : undefined} data-fill={fill ? "" : undefined}>
      <a className="app-skip" href="#main">
        Langsung ke konten
      </a>

      <header className="app-header no-print" data-actions={headerTitle ? "" : undefined}>
        {headerTitle === null ? (
          <a className="app-brand" href={home} aria-label={homeName}>
            <Mark />
            <span className="app-name" aria-hidden="true">
              {PRODUCT_SHORT}
            </span>
          </a>
        ) : (
          <>
            {/* A screen's title stands beside the mark, never inside the
                home link: tapping the heading does not navigate. */}
            <a className="app-brand" data-mark-only="" href={home} aria-label={homeName}>
              <Mark />
            </a>
            {headerHeading ? (
              <h1 className="app-name app-title" title={headerTitle}>
                {headerTitle}
              </h1>
            ) : (
              <p className="app-name app-title" title={headerTitle}>
                {headerTitle}
              </p>
            )}
          </>
        )}
        <div className="app-header-end" ref={setHeaderSlot} />
        {/* Below 768 there is no sidebar: the theme button sits in the header. */}
        {tablet ? null : <ThemeToggle />}
      </header>

      <aside className="app-side no-print" id="side-nav">
        <div className="side-top">
          <a className="side-brand" href={home} aria-label={homeName}>
            <Mark />
            <span className="side-name" aria-hidden="true">
              {PRODUCT_SHORT}
            </span>
          </a>
          <button
            type="button"
            className="ui-icon-btn side-toggle"
            aria-controls="side-nav"
            aria-expanded={!rail}
            aria-label={rail ? "Luaskan menu" : "Ciutkan menu"}
            title={rail ? "Luaskan menu" : "Ciutkan menu"}
            onClick={toggle}
          >
            <Glyph name="sidebarLeft" />
          </button>
        </div>
        <nav className="side-nav" aria-label="Menu utama">
          {/* The admin menu under its group titles (Pengguna, Pengetahuan, Layanan); the konsultan menu has none. */}
          {groupNav(items).map((g, i) => (
            <div key={g.title ?? i} className="side-group">
              {g.title ? (
                <p className="side-group-title" id={`side-group-${i}`}>
                  {g.title}
                </p>
              ) : null}
              <ul className="side-list" aria-labelledby={g.title ? `side-group-${i}` : undefined}>
                {g.items.map((item) => (
                  <SideItem key={item.id} item={item} current={current} />
                ))}
              </ul>
            </div>
          ))}
        </nav>
        <div className="side-foot">
          {tablet ? <div className="side-theme">{wide && !rail ? <ThemeSwitch /> : <ThemeToggle />}</div> : null}
          <div className="side-person-row">
            <a
              className="side-item side-profile"
              href={profileItem.href}
              aria-current={current === "profil" ? "page" : undefined}
              aria-label={`Profil, ${user.name}, ${ROLE_LABEL[user.role]}`}
              title={`${user.name} (${ROLE_LABEL[user.role]})`}
            >
              <span className="side-icon">
                <Glyph name="profileCircle" size={24} />
              </span>
              <span className="side-label side-label-full side-person" aria-hidden="true">
                <span className="side-person-name">{user.name}</span>
                <span className="side-person-role">{ROLE_LABEL[user.role]}</span>
              </span>
              <span className="side-label side-label-short" aria-hidden="true">
                Profil
              </span>
            </a>
            {logoutButton}
          </div>
        </div>
      </aside>

      <main id="main" ref={main} className="app-main" tabIndex={-1} data-ui-scroller={fill ? undefined : ""}>
        {fill ? children : <div className="page" data-layout={layout}>{children}</div>}
      </main>

      <nav className="app-bar no-print" aria-label="Menu utama">
        {bar.map((item) => (
          <a key={item.id} className="bar-item" href={item.href} aria-current={item.id === current ? "page" : undefined} aria-label={item.label}>
            <span className="bar-pill">
              <Glyph name={item.icon} size={24} />
            </span>
            <span className="bar-label" aria-hidden="true">
              {item.short}
            </span>
          </a>
        ))}
        {more.length > 0 ? (
          <button
            type="button"
            className="bar-item"
            aria-haspopup="dialog"
            aria-expanded={moreOpen}
            aria-current={moreActive ? "page" : undefined}
            onClick={() => setMoreOpen(true)}
          >
            <span className="bar-pill">
              <Glyph name="element3" size={24} />
            </span>
            <span className="bar-label">Lainnya</span>
          </button>
        ) : null}
      </nav>

      <Dialog open={moreOpen && more.length > 0} title="Lainnya" size="sm" onClose={() => setMoreOpen(false)}>
        <ul className="more-list" onClick={(e) => (e.target as Element).closest?.("a") && setMoreOpen(false)}>
          {more.map((item) => (
            <li key={item.id} className={item.id === "profil" ? "more-person" : undefined}>
              <a className="more-item" href={item.href} aria-current={item.id === current ? "page" : undefined}>
                <Glyph name={item.icon} size={24} />
                <span>{item.id === "profil" ? `Profil, ${user.name}` : item.label}</span>
              </a>
              {item.id === "profil" ? logoutButton : null}
            </li>
          ))}
        </ul>
      </Dialog>
    </div>
    </ShellContext.Provider>
  );
}
